'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { useRouter } from 'next/navigation';
import { routes } from '@cntxt/domains/access-control';
import { clearSignInAttempts, registerSignInAttempt, SIGN_IN_LOOP_ERROR_CODE } from './signInLoopGuard';
export type SignInLoopGuard = {
  /** Records a sign-in redirect and reports whether it may go ahead. */
  readonly allowSignIn: () => boolean;
  /** Call once a session resolves: the redirects were a login, not a loop. */
  readonly markSignedIn: () => void;
};

/**
 * React binding for {@link registerSignInAttempt} — the choreography both
 * sign-in triggers need: count the redirect, and when sign-in stops producing a
 * session, send the user to the shared sign-in error page rather than starting
 * yet another round.
 *
 * That page is where every other auth failure lands, so the stopped loop gets
 * the same treatment (reference code included) and nothing here needs a screen
 * of its own. It is also an `/auth/*` page, so no auto sign-in or sign-out
 * fires there and the user finally stays put.
 */
export function useSignInLoopGuard() {
  const $ = _c(4);
  const router = useRouter();
  let t0;
  if ($[0] !== router) {
    t0 = () => {
      if (registerSignInAttempt() === "loop-detected") {
        router.replace(`${routes.AUTH_SIGNIN_ERROR.url}?error=${SIGN_IN_LOOP_ERROR_CODE}`);
        return false;
      }
      return true;
    };
    $[0] = router;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const allowSignIn = t0;
  let t1;
  if ($[2] !== allowSignIn) {
    t1 = {
      allowSignIn,
      markSignedIn: clearSignInAttempts
    };
    $[2] = allowSignIn;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}